/**
 * The six variants of components.md §3, with every documented state.
 *
 * Two rules from that table are load-bearing and easy to lose:
 *
 * **Loading keeps the width.** The label is swapped for a 14px spinner and the
 * button does not resize. `BUY UP ▲` becoming a narrow spinner would move the
 * ticket under the user's cursor in the time a marketable order legitimately
 * spends `PENDING` (D12) — which is the exact moment they are most likely to
 * click again.
 *
 * **Disabled always states its reason.** `domain/order-validation.ts` (Phase 6)
 * reads every reason out of `GET /config`; the plan's exit criterion is "a
 * disabled button with a reason, never a surprise rejection". So `disabled`
 * takes an optional `disabledReason`, which becomes the accessible description
 * and the tooltip. A `disabled` with no reason is legitimate for a
 * not-yet-loaded control and nothing else.
 *
 * Transitions are `duration-instant` (80ms) per the design system, and the focus
 * ring is `shadow-focus` — never `outline: none` with nothing behind it.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from '@/lib/cx'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'up' | 'down' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'base' | 'lg'

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-solid text-accent-on-solid hover:bg-accent-solid-hover active:bg-accent-solid-hover',
  up: 'bg-up-solid text-up-on-solid hover:bg-up-solid-hover active:bg-up-solid-hover',
  down: 'bg-down-solid text-down-on-solid hover:bg-down-solid-hover active:bg-down-solid-hover',
  secondary:
    'bg-bg-raised text-text-primary border border-border-default hover:bg-bg-hover hover:border-border-strong active:bg-bg-active',
  ghost: 'bg-transparent text-text-secondary hover:bg-bg-hover hover:text-text-primary active:bg-bg-active',
  // components.md writes this one as "`danger-solid` / white". `danger-solid`
  // *is* `down-9`, so its label colour is `down-on-solid` — which the token
  // builder computed by measuring APCA against both ends of the neutral ramp,
  // and which resolves to white in light and near-white in dark. Hard-coding
  // white is how a 2.5:1 button ships the day the rose gets lighter.
  danger: 'bg-danger-solid text-down-on-solid hover:bg-down-solid-hover active:bg-down-solid-hover',
}

/** Only the solid fills scale on press; a ghost that shrinks reads as a glitch. */
const PRESSES: ReadonlySet<ButtonVariant> = new Set(['primary', 'up', 'down', 'danger'])

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-[var(--ud-control-height-sm)] px-2 text-caption',
  base: 'h-[var(--ud-control-height)] px-3 text-body',
  lg: 'h-[var(--ud-control-height-lg)] px-4 text-body',
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  block?: boolean
  /** Why the button is disabled. Becomes the tooltip and the a11y description. */
  disabledReason?: string
  children: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'base',
  loading = false,
  block = false,
  disabled = false,
  disabledReason,
  className,
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading

  return (
    <button
      type="button"
      {...rest}
      disabled={isDisabled}
      title={disabled && disabledReason ? disabledReason : rest.title}
      aria-describedby={rest['aria-describedby']}
      aria-disabled={isDisabled || undefined}
      data-variant={variant}
      data-loading={loading || undefined}
      className={cx(
        'relative inline-flex items-center justify-center gap-1_5 rounded-md font-semibold whitespace-nowrap select-none',
        'transition-[background-color,border-color,color,transform] duration-[var(--ud-duration-instant)] ease-out',
        'focus-visible:shadow-focus focus-visible:outline-none',
        SIZE[size],
        block && 'w-full',
        isDisabled
          ? 'cursor-not-allowed bg-bg-active text-text-disabled border border-transparent'
          : cx(VARIANT[variant], PRESSES.has(variant) && 'active:scale-[0.98]'),
        className,
      )}
    >
      {/* The label stays in the flow at zero opacity so the button cannot
          change width when the spinner replaces it. */}
      <span className={cx('inline-flex items-center gap-1_5', loading && 'invisible')}>
        {children}
      </span>
      {loading ? (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <Spinner />
        </span>
      ) : null}
      {disabled && disabledReason ? <span className="sr-only">{disabledReason}</span> : null}
    </button>
  )
}
